import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const GiftAnalyticsChart = ({ gifts = [], campaigns = [] }) => {
  // Filter gift and anniversary campaigns
  const giftCampaigns = campaigns.filter(c => c.sendosoEnabled && (c.campaignType === 'gift' || c.campaignType === 'anniversary'));
  
  // Group gifts by campaign and type
  const giftsByCampaign = giftCampaigns.reduce((acc, campaign) => {
    const campaignGifts = gifts.filter(g => g.campaign && g.campaign._id === campaign._id);
    acc[campaign.name] = {
      total: campaignGifts.length,
      byType: campaignGifts.reduce((typeAcc, gift) => {
        typeAcc[gift.giftType] = (typeAcc[gift.giftType] || 0) + 1;
        return typeAcc;
      }, {})
    };
    return acc;
  }, {});

  // Prepare data for the chart
  const data = {
    labels: Object.keys(giftsByCampaign),
    datasets: [
      {
        label: 'Total Gifts',
        data: Object.values(giftsByCampaign).map(c => c.total),
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 1
      }
    ]
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Gifts Sent by Campaign'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Number of Gifts'
        }
      }
    }
  };

  // Calculate gift type distribution
  const giftTypeDistribution = gifts.reduce((acc, gift) => {
    if (gift && gift.giftType) {
      acc[gift.giftType] = (acc[gift.giftType] || 0) + 1;
    }
    return acc;
  }, {});

  if (gifts.length === 0 || campaigns.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No gift data available yet.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-800 mb-2">Gift Type Distribution</h3>
        <div className="grid grid-cols-2 gap-4">
          {Object.entries(giftTypeDistribution).map(([type, count]) => (
            <div key={type} className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm font-medium text-gray-600 capitalize">{type.replace('_', ' ')}</p>
              <p className="text-2xl font-bold text-gray-800">{count}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-800 mb-2">Gifts by Campaign</h3>
        <div className="bg-white p-4 rounded-lg">
          <Line data={data} options={options} />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium text-gray-800 mb-2">Campaign Details</h3>
        <div className="space-y-4">
          {Object.entries(giftsByCampaign).map(([campaignName, stats]) => (
            <div key={campaignName} className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-800">{campaignName}</h4>
              <p className="text-sm text-gray-600">Total Gifts: {stats.total}</p>
              <div className="mt-2">
                {Object.entries(stats.byType).map(([type, count]) => (
                  <p key={type} className="text-sm text-gray-600 capitalize">
                    {type.replace('_', ' ')}: {count}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GiftAnalyticsChart;
